<script lang="ts">
import type { StyleValue } from 'vue';

export type TimeInputV2Props = {
  id?: string;
  /**
   * Display format for the input value.
   *
   * Tokens: `HH` (2-digit hour), `mm` (2-digit minute), `A` (AM/PM upper), `a` (am/pm lower).
   *
   * If `A`/`a` is present the hour is rendered in 12h, otherwise 24h. Defaults to locale format.
   */
  format?: string;
  placeholder?: string;
  disabled?: boolean;
  /** BCP-47 locale used for the dropdown labels. Defaults to the i18n injection locale. */
  locale?: string;
  /** ISO `HH:MM` lower bound (inclusive). */
  minValue?: string;
  /** ISO `HH:MM` upper bound (inclusive). */
  maxValue?: string;
  wrapperStyle?: StyleValue;
  height?: InputHeight;
  /**
   * When `true`, the clock icon is hidden until the field is hovered or focused,
   * then fades in — its space stays reserved, so nothing shifts. Useful in dense
   * layouts (e.g. table cells) to cut visual noise. Opt-in; defaults to `false`.
   */
  revealIcon?: boolean;
};

function getLocalTimeFormat(locale: string) {
  const parts = new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(new Date(2000, 0, 2, 13, 5));
  const partReplacements = { hour: 'HH', minute: 'mm', dayPeriod: 'A' } as const;
  const allowedParts = [...Object.keys(partReplacements), 'literal'];
  return parts
    .filter((p) => allowedParts.includes(p.type))
    .map((p) =>
      p.type === 'literal' ? p.value : partReplacements[p.type as keyof typeof partReplacements]
    )
    .join('');
}
</script>

<script setup lang="ts">
import { ClockIcon } from '@blpde/nx-icons';
import { computed, inject, nextTick, onUnmounted, ref, useId, useTemplateRef, watch } from 'vue';
import { NxButton } from '../Button';
import { NxDropdown } from '../Dropdown';
import {
  FONT_SIZE_LOOKUP,
  i18nInjectionKey,
  InputHeight,
  labelInjectionKey,
  loadingStateInjectionKey,
} from './internal';
import { formatPlaceholder } from './formatPlaceholder';
import NxTextInput from './NxTextInput.vue';

const props = withDefaults(defineProps<TimeInputV2Props>(), {
  format: undefined,
  id: undefined,
  placeholder: undefined,
  locale: undefined,
  minValue: undefined,
  maxValue: undefined,
  wrapperStyle: undefined,
  height: undefined,
  revealIcon: false,
});

const valueModel = defineModel<string | null>({ required: true });

const i18nInjection = inject(i18nInjectionKey)!;
const loadingState = inject(loadingStateInjectionKey, null);

let elementId = props.id;
const labelInjection = inject(labelInjectionKey, null);
if (labelInjection && !labelInjection.for.value) {
  elementId ??= useId();
  labelInjection.setFor(elementId);
}

const disabled = computed(() => !!(props.disabled || loadingState?.status.value?.disabled));
const locale = computed(() => props.locale ?? i18nInjection.locale.value);
const formatInternal = computed(() => props.format ?? getLocalTimeFormat(locale.value));
const placeholderInternal = computed(
  () => props.placeholder ?? formatPlaceholder(formatInternal.value, locale.value)
);
const is12h = computed(() => /[Aa]/.test(formatInternal.value));

type TimeParts = { hour: number; minute: number };

const pad2 = (n: number) => String(n).padStart(2, '0');

const isValidTime = (p: TimeParts): boolean =>
  p.hour >= 0 && p.hour <= 23 && p.minute >= 0 && p.minute <= 59;

const toIsoTime = (p: TimeParts): string => `${pad2(p.hour)}:${pad2(p.minute)}`;

const parseIsoTime = (iso: string | null | undefined): TimeParts | null => {
  if (!iso) return null;
  const m = /^(\d{2}):(\d{2})/.exec(iso);
  if (!m) return null;
  const parts = { hour: Number(m[1]), minute: Number(m[2]) };
  return isValidTime(parts) ? parts : null;
};

const nowParts = (): TimeParts => {
  const d = new Date();
  return { hour: d.getHours(), minute: d.getMinutes() };
};

const formatDisplay = (p: TimeParts | null): string => {
  if (!p) return '';
  let h = p.hour;
  let amPm = '';
  if (is12h.value) {
    amPm = h < 12 ? 'AM' : 'PM';
    if (h === 0) h = 12;
    else if (h > 12) h -= 12;
  }
  return formatInternal.value
    .replace(/HH/g, pad2(h))
    .replace(/mm/g, pad2(p.minute))
    .replace(/A/g, amPm)
    .replace(/a/g, amPm.toLowerCase());
};

/**
 * Parses freeform user input into an ISO `HH:MM` time string.
 * - `''` → `null`
 * - `'0'` → now
 * - 1-2 digits → hour, minute = 0
 * - 3 digits → H + MM
 * - 4 digits → HH + MM
 *
 * Trailing `am`/`pm` shifts hour into 24h. Returns `undefined` if not parseable.
 */
const parseInput = (raw: string): string | null | undefined => {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (trimmed === '0') return toIsoTime(nowParts());

  const lower = trimmed.toLowerCase();
  let ampm: 'am' | 'pm' | null = null;
  if (/p\.?\s*m\.?\s*$/.test(lower)) ampm = 'pm';
  else if (/a\.?\s*m\.?\s*$/.test(lower)) ampm = 'am';

  const digits = trimmed.replace(/\D/g, '');
  if (!digits) return undefined;

  let hour: number;
  let minute: number;
  switch (digits.length) {
    case 1:
    case 2:
      hour = Number(digits);
      minute = 0;
      break;
    case 3:
      hour = Number(digits.slice(0, 1));
      minute = Number(digits.slice(1, 3));
      break;
    case 4:
      hour = Number(digits.slice(0, 2));
      minute = Number(digits.slice(2, 4));
      break;
    default:
      return undefined;
  }

  if (ampm) {
    if (hour < 1 || hour > 12) return undefined;
    if (ampm === 'pm' && hour !== 12) hour += 12;
    if (ampm === 'am' && hour === 12) hour = 0;
  }

  const parts = { hour, minute };
  if (!isValidTime(parts)) return undefined;
  return toIsoTime(parts);
};

const focused = ref(false);
const rawInput = ref('');
const parseError = ref(false);
let parseErrorTimeout: ReturnType<typeof setTimeout> | null = null;

const displayValue = computed<string>({
  get() {
    if (focused.value) return rawInput.value;
    return formatDisplay(parseIsoTime(valueModel.value));
  },
  set(v) {
    rawInput.value = v;
  },
});

const onFocus = () => {
  focused.value = true;
  rawInput.value = formatDisplay(parseIsoTime(valueModel.value));
};

const onBlur = () => {
  focused.value = false;
  const parsed = parseInput(rawInput.value);
  if (parsed === undefined) {
    if (parseErrorTimeout) clearTimeout(parseErrorTimeout);
    parseError.value = true;
    parseErrorTimeout = setTimeout(() => {
      parseError.value = false;
      parseErrorTimeout = null;
    }, 1500);
    return;
  }
  if (parsed !== valueModel.value) {
    valueModel.value = parsed;
  }
};

const isOpen = ref(false);
const containerRef = useTemplateRef<HTMLElement>('container-ref');

const openDropdown = () => {
  if (disabled.value) return;
  isOpen.value = true;
};
const closeDropdown = () => {
  isOpen.value = false;
};

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = Array.from({ length: 12 }, (_, i) => i * 5);

/**
 * Both grids share one coordinate system for keyboard navigation: the hours
 * occupy columns 0..3, the minutes columns 4..6, so moving between them is just
 * a column step. The hour grid is the taller one (6 rows vs. 4).
 */
const HOUR_COLS = 4;
const MINUTE_COLS = 3;
const HOUR_ROWS = HOURS.length / HOUR_COLS;
const MINUTE_ROWS = MINUTES.length / MINUTE_COLS;
const MINUTE_COL_OFFSET = HOUR_COLS;
const LAST_MINUTE_COL = MINUTE_COL_OFFSET + MINUTE_COLS - 1;

const queryTimeCell = (row: number, col: number): HTMLButtonElement | null =>
  containerRef.value?.querySelector<HTMLButtonElement>(
    `.time-cell[data-cell-row="${row}"][data-cell-col="${col}"]`
  ) ?? null;

const queryQuickCell = (index: number): HTMLButtonElement | null =>
  containerRef.value?.querySelector<HTMLButtonElement>(
    `.time-quick-cell[data-quick-index="${index}"]`
  ) ?? null;

const focusEl = (el: HTMLButtonElement | null) => {
  if (!el || el.disabled) return;
  el.focus();
};

/**
 * Focus the minute cell holding `minute`. Typed-in values can sit between the
 * five-minute steps the grid offers (e.g. `08:07`); those fall back to `:00`, so
 * the focus always has somewhere to land.
 */
const focusMinuteCell = (minute: number) => {
  const index = MINUTES.indexOf(minute);
  const slot = index === -1 ? 0 : index;
  focusEl(queryTimeCell(Math.floor(slot / MINUTE_COLS), MINUTE_COL_OFFSET + (slot % MINUTE_COLS)));
};

const isHourDisabled = (h: number): boolean => {
  if (props.minValue && `${pad2(h)}:59` < props.minValue) return true;
  if (props.maxValue && `${pad2(h)}:00` > props.maxValue) return true;
  return false;
};

const isMinuteDisabled = (m: number): boolean => {
  const cur = parseIsoTime(valueModel.value);
  if (!cur) return false;
  const t = `${pad2(cur.hour)}:${pad2(m)}`;
  if (props.minValue && t < props.minValue) return true;
  if (props.maxValue && t > props.maxValue) return true;
  return false;
};

const isTimeDisabled = (p: TimeParts): boolean => {
  const t = toIsoTime(p);
  if (props.minValue && t < props.minValue) return true;
  if (props.maxValue && t > props.maxValue) return true;
  return false;
};

const selectedHour = computed(() => parseIsoTime(valueModel.value)?.hour ?? null);
const selectedMinute = computed(() => parseIsoTime(valueModel.value)?.minute ?? null);

const selectHour = (h: number) => {
  if (isHourDisabled(h)) return;
  const cur = parseIsoTime(valueModel.value) ?? { hour: 0, minute: 0 };
  valueModel.value = toIsoTime({ hour: h, minute: cur.minute });
  // Picking the hour is only half the entry, so hand the keyboard straight over
  // to the minute grid — onto the cell the (possibly just defaulted) minute of
  // the value points at. Deferred: the minutes' disabled state is derived from
  // the hour just written, and a disabled cell would refuse the focus.
  nextTick(() => focusMinuteCell(cur.minute));
};

const selectMinute = (m: number) => {
  const cur = parseIsoTime(valueModel.value) ?? { hour: nowParts().hour, minute: 0 };
  if (isTimeDisabled({ hour: cur.hour, minute: m })) return;
  const next = { hour: cur.hour, minute: m };
  valueModel.value = toIsoTime(next);
  rawInput.value = formatDisplay(next);
  closeDropdown();
};

const setNow = () => {
  const n = nowParts();
  if (isTimeDisabled(n)) return;
  valueModel.value = toIsoTime(n);
  rawInput.value = formatDisplay(n);
  closeDropdown();
};

const addMinutes = (delta: number) => {
  const base = parseIsoTime(valueModel.value) ?? nowParts();
  const total = (((base.hour * 60 + base.minute + delta) % (24 * 60)) + 24 * 60) % (24 * 60);
  const next = { hour: Math.floor(total / 60), minute: total % 60 };
  if (isTimeDisabled(next)) return;
  valueModel.value = toIsoTime(next);
  rawInput.value = formatDisplay(next);
  closeDropdown();
};

/** Grid cell a quick action returns to with ArrowUp — the inverse of `quickIndexBelow`. */
const QUICK_RETURN: readonly (readonly [number, number])[] = [
  [HOUR_ROWS - 1, 0], // Jetzt -> 20
  [HOUR_ROWS - 1, 2], // +15   -> 22
  [MINUTE_ROWS - 1, MINUTE_COL_OFFSET], // +30 -> 45
  [MINUTE_ROWS - 1, LAST_MINUTE_COL], // +1h   -> 55
];

const isHourCol = (col: number) => col < MINUTE_COL_OFFSET;

/** Quick action reached with ArrowDown out of the bottom row of either grid. */
const quickIndexBelow = (col: number): number => {
  if (isHourCol(col)) return col <= 1 ? 0 : 1; // 20,21 -> Jetzt | 22,23 -> +15
  return col === MINUTE_COL_OFFSET ? 2 : 3; // 45 -> +30 | 50,55 -> +1h
};

const onCellKeydown = (e: KeyboardEvent, target: HTMLElement) => {
  const rowAttr = target.getAttribute('data-cell-row');
  const colAttr = target.getAttribute('data-cell-col');
  if (rowAttr === null || colAttr === null) return;
  const row = Number(rowAttr);
  const col = Number(colAttr);
  const lastRow = (isHourCol(col) ? HOUR_ROWS : MINUTE_ROWS) - 1;
  let next: HTMLButtonElement | null = null;
  switch (e.key) {
    case 'ArrowLeft':
      // Leaving the minute grid to the left re-enters the hours in the same row.
      if (col > 0) next = queryTimeCell(row, col - 1);
      break;
    case 'ArrowRight':
      // The hour grid is taller than the minute grid, so its two extra rows
      // (19 and 23) enter the minutes at their last row instead.
      if (col === MINUTE_COL_OFFSET - 1)
        next = queryTimeCell(Math.min(row, MINUTE_ROWS - 1), MINUTE_COL_OFFSET);
      else if (col < LAST_MINUTE_COL) next = queryTimeCell(row, col + 1);
      break;
    case 'ArrowUp':
      if (row > 0) next = queryTimeCell(row - 1, col);
      break;
    case 'ArrowDown':
      // The bottom row leaves the grids downwards onto the quick-action row.
      next = row === lastRow ? queryQuickCell(quickIndexBelow(col)) : queryTimeCell(row + 1, col);
      break;
    default:
      return;
  }
  e.preventDefault();
  e.stopPropagation();
  focusEl(next);
};

const onQuickKeydown = (e: KeyboardEvent, target: HTMLElement) => {
  const idxAttr = target.getAttribute('data-quick-index');
  if (idxAttr === null) return;
  const idx = Number(idxAttr);
  let next: HTMLButtonElement | null = null;
  switch (e.key) {
    case 'ArrowUp': {
      const cell = QUICK_RETURN[idx];
      if (cell) next = queryTimeCell(cell[0], cell[1]);
      break;
    }
    case 'ArrowLeft':
      if (idx > 0) next = queryQuickCell(idx - 1);
      break;
    case 'ArrowRight':
      if (idx < QUICK_RETURN.length - 1) next = queryQuickCell(idx + 1);
      break;
    default:
      return;
  }
  e.preventDefault();
  e.stopPropagation();
  focusEl(next);
};

/**
 * Arrow-key navigation inside the open dropdown.
 *
 * Bound on the picker root rather than the grids so the quick-action row takes
 * part in it — it is a sibling of the grids, so a handler on them would never
 * see its key events.
 */
const onPickerKeydown = (e: KeyboardEvent) => {
  const target = e.target as HTMLElement | null;
  if (!target) return;
  if (target.classList.contains('time-cell')) onCellKeydown(e, target);
  else if (target.classList.contains('time-quick-cell')) onQuickKeydown(e, target);
};

const onDocumentMousedown = (e: MouseEvent) => {
  const target = e.target as Node | null;
  if (!target) return;
  if (containerRef.value?.contains(target)) return;
  closeDropdown();
};

const onKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Escape' && isOpen.value) {
    e.preventDefault();
    closeDropdown();
  }
};

/**
 * Move focus into the grids when the dropdown opens: onto the selected hour when
 * the model holds one (it precedes the selected minute in the DOM), otherwise
 * onto the first selectable cell — focus must not stay behind in the text field,
 * where the arrow keys above would never see it.
 */
const focusInitialCell = () => {
  const root = containerRef.value;
  if (!root) return;
  const target =
    root.querySelector<HTMLButtonElement>('.time-cell[data-selected]:not(:disabled)') ??
    root.querySelector<HTMLButtonElement>('.time-cell:not(:disabled)');
  target?.focus({ focusVisible: true } as FocusOptions);
};

watch(isOpen, (open) => {
  if (open) {
    setTimeout(() => {
      document.addEventListener('mousedown', onDocumentMousedown, true);
      document.addEventListener('keydown', onKeydown, true);
      focusInitialCell();
    }, 0);
  } else {
    document.removeEventListener('mousedown', onDocumentMousedown, true);
    document.removeEventListener('keydown', onKeydown, true);
  }
});

onUnmounted(() => {
  document.removeEventListener('mousedown', onDocumentMousedown, true);
  document.removeEventListener('keydown', onKeydown, true);
  if (parseErrorTimeout) clearTimeout(parseErrorTimeout);
});

const hourLabel = (h: number): string => {
  if (!is12h.value) return pad2(h);
  let display = h % 12;
  if (display === 0) display = 12;
  return pad2(display);
};

const unitLabel = (unit: 'hour' | 'minute'): string => {
  const parts = new Intl.NumberFormat(locale.value, {
    style: 'unit',
    unit,
    unitDisplay: 'short',
  }).formatToParts(0);
  return parts.find((p) => p.type === 'unit')?.value ?? unit;
};

const hourLabelText = computed(() => unitLabel('hour'));
const minuteLabelText = computed(() => unitLabel('minute'));
</script>

<template>
  <div ref="container-ref" :class="['nx-time-input-v2', { 'parse-error': parseError }]">
    <NxDropdown :is-open="isOpen" align="end" @close="closeDropdown">
      <template #trigger>
        <NxTextInput
          :id="elementId"
          v-model="displayValue"
          :icons="[
            {
              align: 'end',
              component: ClockIcon,
              filled: true,
              // Always functional (see NxDateInput): `deco: false` keeps it an
              // inert action button when disabled instead of a decorative span,
              // so reveal-on-interaction survives the disabled state.
              deco: false,
              color: disabled
                ? 'var(--nxt-border-strong)'
                : isOpen
                  ? 'var(--nxt-brand-bg)'
                  : undefined,
              onClick: disabled ? undefined : () => (isOpen ? closeDropdown() : openDropdown()),
              revealOnInteraction: props.revealIcon,
            },
          ]"
          :wrapper-style="props.wrapperStyle"
          :height="props.height"
          :placeholder="placeholderInternal"
          :disabled="disabled"
          :data-focus-visible="isOpen ? true : undefined"
          @focus="onFocus"
          @blur="onBlur"
        />
      </template>
      <div
        class="time-picker"
        :style="`--font-size: ${FONT_SIZE_LOOKUP[props.height ?? 's']}`"
        @keydown="onPickerKeydown"
      >
        <div class="time-picker-grids">
          <div class="time-grid" :style="`--row-count: ${HOUR_ROWS}; --col-count: ${HOUR_COLS};`">
            <div class="time-grid-label">{{ hourLabelText }}</div>
            <button
              v-for="h in HOURS"
              :key="`h-${h}`"
              type="button"
              class="time-cell"
              :data-cell-row="Math.floor(h / HOUR_COLS)"
              :data-cell-col="h % HOUR_COLS"
              :data-selected="selectedHour === h ? '' : undefined"
              :data-disabled="isHourDisabled(h) ? '' : undefined"
              :disabled="isHourDisabled(h)"
              @mousedown.prevent
              @click="selectHour(h)"
            >
              {{ hourLabel(h) }}
            </button>
          </div>
          <div class="grid-divider"></div>
          <div
            class="time-grid"
            :style="`--row-count: ${MINUTE_ROWS}; --col-count: ${MINUTE_COLS};`"
          >
            <div class="time-grid-label">{{ minuteLabelText }}</div>
            <button
              v-for="(m, mIdx) in MINUTES"
              :key="`m-${m}`"
              type="button"
              class="time-cell"
              :data-cell-row="Math.floor(mIdx / MINUTE_COLS)"
              :data-cell-col="MINUTE_COL_OFFSET + (mIdx % MINUTE_COLS)"
              :data-selected="selectedMinute === m ? '' : undefined"
              :data-disabled="isMinuteDisabled(m) ? '' : undefined"
              :disabled="isMinuteDisabled(m)"
              @mousedown.prevent
              @click="selectMinute(m)"
            >
              {{ pad2(m) }}
            </button>
          </div>
        </div>
        <div class="time-quick-row">
          <NxButton
            class="time-quick-cell"
            :data-quick-index="0"
            height="xs"
            width="narrow"
            variant="ghost"
            :disabled="disabled"
            @mousedown.prevent
            @click="setNow"
          >
            <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
            {{ 'Jetzt' }}
          </NxButton>
          <NxButton
            class="time-quick-cell"
            :data-quick-index="1"
            height="xs"
            width="narrow"
            variant="ghost"
            :disabled="disabled"
            @mousedown.prevent
            @click="addMinutes(15)"
          >
            <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
            {{ '+15' }}
          </NxButton>
          <NxButton
            class="time-quick-cell"
            :data-quick-index="2"
            height="xs"
            width="narrow"
            variant="ghost"
            :disabled="disabled"
            @mousedown.prevent
            @click="addMinutes(30)"
          >
            <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
            {{ '+30' }}
          </NxButton>
          <NxButton
            class="time-quick-cell"
            :data-quick-index="3"
            height="xs"
            width="narrow"
            variant="ghost"
            :disabled="disabled"
            @mousedown.prevent
            @click="addMinutes(60)"
          >
            <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
            {{ '+1h' }}
          </NxButton>
        </div>
      </div>
    </NxDropdown>
  </div>
</template>

<style scoped>
.nx-time-input-v2 {
  width: 100%;
}

.nx-time-input-v2.parse-error :deep(input) {
  animation: nx-time-input-parse-error 1.5s ease-out forwards;
}

@keyframes nx-time-input-parse-error {
  0%,
  60% {
    border-color: var(--nxt-danger-bg);
    background-color: var(--nxt-danger-subtle-bg);
    box-shadow: 0 0 0 calc(0.125em / 0.875) var(--nxt-input-ring-invalid);
  }
  100% {
    border-color: var(--nxt-border);
    background-color: var(--nxt-input-bg);
    box-shadow: none;
  }
}

.time-picker {
  font-size: var(--font-size);
  border-radius: var(--nxt-radius-md);
  padding: var(--nxt-space-sm);
  background-color: var(--nxt-popover-bg);
  border: solid var(--nxt-hairline) var(--nxt-popover-border);
  box-shadow: var(--nxt-popover-shadow);
  display: flex;
  flex-direction: column;
  width: fit-content;
  margin-left: auto;
  user-select: none;
}

.time-picker-grids {
  display: flex;
  flex-direction: row;
  gap: var(--nxt-space-xs);
}

.grid-divider {
  align-self: stretch;
  width: var(--nxt-hairline);
  background-color: var(--nxt-divider-color);
}

.time-grid {
  display: grid;
  justify-items: center;
  align-items: center;
  grid-template-columns: repeat(var(--col-count), 1.1lh);
  grid-template-rows: repeat(var(--row-count), 1.1lh);
  gap: calc(
    var(--nxt-size-unit) * 0.0625
  ); /* stylelint-disable-line declaration-property-unit-disallowed-list -- 1px optical nudge */
  width: fit-content;
  height: fit-content;
}

.time-grid-label {
  grid-column: 1 / -1;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-weight: var(--nxt-font-weight-medium);
  color: var(--nxt-brand-bg);
}

.time-cell {
  color: var(--nxt-fg);
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  white-space: nowrap;
  border-radius: var(--nxt-radius-sm);
  border: var(--nxt-hairline) solid transparent;
  background-color: transparent;
  cursor: pointer;

  &[data-selected] {
    background-color: var(--nxt-brand-bg);
    color: var(--nxt-brand-fg);
    font-weight: var(--nxt-font-weight-medium);
  }
  &[data-outside-view] {
    color: var(--nxt-fg-subtle);
  }
  &[data-disabled],
  &:disabled {
    cursor: default;
    color: var(--nxt-fg-muted);
    pointer-events: none;
  }
  &[data-today] {
    text-decoration: underline;
  }
  &:hover,
  &:focus-visible {
    outline: 0;
    border: var(--nxt-hairline) solid var(--nxt-brand-bg);
  }

  &[data-selected]:focus-visible {
    box-shadow: inset 0 0 0 var(--nxt-hairline) var(--nxt-brand-fg);
  }
}

.time-quick-row {
  display: flex;
  flex-direction: row;
  gap: var(--nxt-space-2xs);
  margin-top: var(--nxt-space-xs);
  justify-content: space-between;
}
</style>
